/**
 * 約定結果タブの client 集計ロジック (= 純関数)。
 *
 * 約定明細は「注文単位の集計約定」 (= `matched_volume_mwh` /
 * `matched_price_yen_per_kwh`) で表現する。 約定ごとの内訳 (約定日時 / 約定番号の全件) は
 * AC-BIN-022 の領分で、 ここでは扱わない。
 */
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { undefinedEnglishCsvHeaderT } from '~/models/csvHeaderLanguage'
import { decimalToNumber } from '~/models/decimal'
import { sumKomaMwToMwh } from '~/models/komaQuantity'

/** 約定済 (= 全量約定 / 部分約定、または部分約定のあと取り消して約定量が残る) か (#8728)。 */
export function isExecuted(row: IntradayOrderRow): boolean {
  if (
    row.bidStatus === BiddingIntradayOrderStatus.ALL_MATCHED ||
    row.bidStatus === BiddingIntradayOrderStatus.PARTIAL_MATCHED
  ) {
    return true
  }
  return (
    row.bidStatus === BiddingIntradayOrderStatus.CANCELLED &&
    (decimalToNumber(row.matchedVolumeMwh) ?? 0) > 0
  )
}

/**
 * 約定結果の約定ステータス (AC-BIN-022a: 約定 / 部分約定の 2 値)。 取消のまま約定量が残る札
 * (#8728) は約定量が入札量に達していれば約定、 それ以外は部分約定とする。
 */
export function executionStatus(
  row: IntradayOrderRow,
): BiddingIntradayOrderStatus {
  if (row.bidStatus !== BiddingIntradayOrderStatus.CANCELLED) {
    return row.bidStatus
  }
  const matched = decimalToNumber(row.matchedVolumeMwh) ?? 0
  const volume = decimalToNumber(row.volumeMwh) ?? 0
  return matched >= volume
    ? BiddingIntradayOrderStatus.ALL_MATCHED
    : BiddingIntradayOrderStatus.PARTIAL_MATCHED
}

/**
 * 既定の並び順: 対象コマ昇順 → エリア → 売買 (買 < 売)。
 * インタラクティブソートは本タスク対象外 (= 既定並びのみ)。
 */
export function sortExecutionRows(
  rows: IntradayOrderRow[],
): IntradayOrderRow[] {
  return [...rows].sort(
    (a, b) =>
      a.timeCode - b.timeCode ||
      a.areaCode - b.areaCode ||
      a.direction - b.direction,
  )
}

/**
 * CSV の値の整形に渡すラベル解決関数群 (= 表示言語の区分値の変換を model に持ち込まないため注入)。
 * ヘッダー行は英語名が未定義のため注入せず、`buildExecutionCsv` が日本語で出す。
 */
export type ExecutionCsvLabels = {
  timeCode: (timeCode: number) => string
  area: (areaCode: AreaCode) => string
  side: (direction: Side) => string
  status: (status: BiddingIntradayOrderStatus) => string
}

/**
 * 1 コマあたりの約定量 MW (AC-BIN-022a の「約定量(MW)」列)。 保持値がそのまま
 * コマ別 MW なので換算しない (issue #4285。 列名 `matched_volume_mwh` は歴史的経緯)。
 */
function matchedVolumeMw(row: IntradayOrderRow): number | undefined {
  return decimalToNumber(row.matchedVolumeMwh)
}

/**
 * 注文単位の約定量合計 MW (AC-BIN-022a の「約定量合計(MW)」列、 AC-BIN-043)。
 * コマ別数量の単純合算で、 MWh へは換算しない (2026-09-08 決定 B)。 現状は
 * 1 注文 = 1 コマ なので約定量(MW) と同じ値になる。
 */
export function matchedVolumeTotalMw(
  row: IntradayOrderRow,
): number | undefined {
  return decimalToNumber(row.matchedVolumeMwh)
}

/**
 * 注文単位の約定電力量 MWh (約定結果 CSV の「約定電力量(MWh)」列、 AC-BIN-023 /
 * AC-BSP-115)。 約定量合計 (MW) × 0.5h で、 照合に使うため丸めない。
 */
function matchedVolumeMwh(row: IntradayOrderRow): number | undefined {
  const mw = matchedVolumeTotalMw(row)
  return mw === undefined ? undefined : sumKomaMwToMwh(mw)
}

/**
 * 残数量 (MW) (AC-BIN-022a の「残数量(MW)」列)。 backend が返す札の値 (注文量 − 約定量。
 * 札が終わった後は約定しなかった数量、 #8736) をそのまま使う。
 */
export function remainingVolumeMw(row: IntradayOrderRow): number | undefined {
  return decimalToNumber(row.remainingVolumeMw)
}

/** 数値 Decimal を CSV セル文字列へ (= 未設定は空文字)。 */
function csvNum(value: number | undefined): string {
  return value === undefined ? '' : String(value)
}

/**
 * 約定明細テーブルの行を約定結果 CSV 文字列 (header + rows) に整形する。 列は
 * AC-BSP-115 (AC-BIN-023) の一部で、 残りの列・期間指定・ファイル名は #9326 で揃える。
 * 表示順 (= 渡された rows の順) をそのまま使う。 BOM 付与は呼び出し側 (download 時)。
 * 列は JEPX 約定番号を持たない (約定結果 CSV は AC-BIN-023 で別に作り直す)。
 */
export function buildExecutionCsv(
  rows: IntradayOrderRow[],
  labels: ExecutionCsvLabels,
): string {
  // ヘッダー行は英語のヘッダー名が spec で未定義のため、表示言語に関わらず日本語で出す
  // (`spot-bidding.md` AC-BSP-115 注記「CSV の項目名・ファイル名は、英語名を定めるまで
  // 英語表示でも日本語で出力する」、`conventions.md`「CSV の英語表示」)。区分値は表示言語。
  const th = undefinedEnglishCsvHeaderT()
  const head = [
    th('page:biddingIntraday.executionResult.detail.timeCode'),
    th('page:biddingIntraday.executionResult.detail.area'),
    th('page:biddingIntraday.executionResult.detail.direction'),
    th('page:biddingIntraday.executionResult.detail.price'),
    th('page:biddingIntraday.executionResult.detail.volumeMw'),
    th('page:biddingIntraday.executionResult.detail.volumeMwh'),
    th('page:biddingIntraday.executionResult.detail.remainingVolume'),
    th('page:biddingIntraday.executionResult.detail.status'),
  ].join(',')

  const body = rows.map((r) =>
    [
      labels.timeCode(r.timeCode),
      labels.area(r.areaCode),
      labels.side(r.direction),
      csvNum(decimalToNumber(r.matchedPriceYenPerKwh)),
      csvNum(matchedVolumeMw(r)),
      csvNum(matchedVolumeMwh(r)),
      csvNum(remainingVolumeMw(r)),
      labels.status(executionStatus(r)),
    ].join(','),
  )

  return [head, ...body].join('\n')
}
